<template>
  <VcLayout column class="calls-widget-content">
    <VcLayout class="calls-widget-content__main">
      <!-- Recent Calls List -->
      <VcLayout column class="calls-widget-content__calls">
        <div class="calls-widget-content__section-title">
          {{ $t('widget.calls_widget.recent_calls') }}
        </div>
        <div v-if="isLoadingCalls" class="calls-widget-content__loading">
          <VcLoader />
        </div>
        <div v-else class="calls-widget-content__calls-list">
          <CallItem
            v-for="(call, index) in recentCalls"
            :key="call.id"
            :call="call"
            :class="{ 'call-item--hide-on-small': index >= 3 }"
            @click="handleCallClick(call)"
          />
        </div>
      </VcLayout>

      <!-- Statistics -->
      <VcLayout class="calls-widget-content__stats">
        <div class="calls-widget-content__stat-item">
          <div class="calls-widget-content__section-title">
            {{ $t('widget.calls_widget.last_30_days') }}
          </div>
          <div class="calls-widget-content__stat-value">
            <VcLoader v-if="isLoadingStats" />
            <span v-else>{{ totalCallsFormatted }}</span>
          </div>
        </div>

        <div
          class="calls-widget-content__stat-item calls-widget-content__stat-item--clickable"
          @click="handleMissedCallsClick"
          @keydown.enter="handleMissedCallsClick"
          tabindex="0"
          role="button"
          :aria-label="'Clients to call back'"
        >
          <div class="calls-widget-content__section-title">
            Clients to call back
          </div>
          <div class="calls-widget-content__stat-value">
            <VcLoader v-if="isLoadingStats" />
            <span v-else>{{ missedCallsPendingFormatted }}</span>
          </div>
        </div>
      </VcLayout>
    </VcLayout>
  </VcLayout>
</template>

<script>
import CallItem from './CallItem.vue';

export default {
  name: 'CallsWidgetContent',
  components: {
    CallItem,
  },
  props: {
    isLoadingStats: {
      type: Boolean,
      default: false,
    },
    isLoadingCalls: {
      type: Boolean,
      default: false,
    },
    callStats: {
      type: Object,
      default: null,
    },
    recentCalls: {
      type: Array,
      default: () => [],
    },
    totalCalls: {
      type: Number,
      default: 0,
    },
    missedCallsPending: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    totalCallsFormatted() {
      return this.totalCalls || 0;
    },
    missedCallsPendingFormatted() {
      return this.missedCallsPending || 0;
    },
  },
  methods: {
    handleCallClick(call) {
      if (call.client_uid) {
        this.$emit('trackEvent', 'Call Item Click');
        this.$emit('navigate', {
          path: `/app/clients/${call.client_uid}`,
        });
      }
    },
    handleMissedCallsClick() {
      this.$emit('trackEvent', 'Clients To Call Back Click');
      this.$emit('navigate', {
        path: '/app/clients',
        queryParams: { view: 'missed_calls' },
      });
    },
  },
};
</script>

<style scoped lang="scss">
$small: 505px;

.calls-widget-content {
  width: 100%;
  height: 100%;
  container-type: inline-size;

  &__main {
    gap: var(--size-value8);
    height: 100%;

    @container (max-width: #{$small}) {
      display: flex;
      flex-direction: column;
      gap: 0;
    }
  }

  &__calls {
    flex: 2;
    min-width: 0;

    // On small screens, take only needed space
    @container (max-width: #{$small}) {
      flex: 0 1 auto;
    }
  }

  &__stats {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 141px;
    gap: var(--size-value8);

    @container (max-width: #{$small}) {
      flex: 0 0 auto;
      flex-direction: row;
      margin-top: auto;

      .calls-widget-content__stat-item {
        flex: 1;
        min-width: 0;
      }
    }
  }

  &__section-title {
    font-size: var(--font-size-x-small);
    font-weight: var(--font-weight-large);
    line-height: 20px;
    color: var(--gray-darken-3);
    margin-bottom: var(--size-value2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 120px;
  }

  &__calls-list {
    display: flex;
    flex-direction: column;
    gap: var(--size-value2);

    // Hide 4th call item on small screens
    .call-item--hide-on-small {
      @container (max-width: #{$small}) {
        display: none;
      }
    }
  }

  &__stat-item {
    display: flex;
    flex-direction: column;

    &--clickable {
      cursor: pointer;

      &:hover {
        .calls-widget-content__section-title,
        .calls-widget-content__stat-value {
          color: var(--link-color);
        }
      }
    }
  }

  &__stat-value {
    font-size: var(--font-size-medium2);
    font-weight: var(--font-weight-large2);
    line-height: 32px;
    color: var(--gray-darken-5);
    letter-spacing: 0.24px;
  }
}
</style>
